import { useRoutePageTitle } from '../../lib/documentTitle';
import type { UseFilesMenuDeps } from './menu/useFilesMenuAction';
import { useFilesMenuAction } from './menu/useFilesMenuAction';
import { useFilesListQuery } from './data/useFilesListQuery';
import { useFilesOfflinePolling } from './offline/useFilesOfflinePolling';
import { useFilesWebSocket } from './realtime/useFilesWebSocket';
import { useFilesKeyboardNavigation } from '../useFilesKeyboardNavigation';
import type { UseFilesProps } from './types';

/**
 * Files list hook for a route/table context: wires list query state, desktop-agent offline polling,
 * WebSocket-driven row refreshes, and the context-menu / bulk-action dispatcher.
 *
 * @param props - `table` selects API list semantics; optional `folderUid` scopes the list to a folder.
 * @returns Everything from {@link useFilesListQuery} except internal `filesActions`, plus offline fields
 *          and `handleFileMenuAction`. The facade strips `filesActions` from the public surface.
 */
function useFiles(props: UseFilesProps) {
  const query = useFilesListQuery(props);
  const { filesActions, ...queryPublic } = query;
  const { folderUid } = props;
  const { parentFolder, isLoading, isNavigating } = queryPublic;

  // Folder route tab title from existing list parent metadata (section root keeps route-rule title).
  const folderMatchesRoute =
    !!folderUid && parentFolder != null && String(parentFolder.id) === String(folderUid);
  useRoutePageTitle({
    enabled: !!folderUid,
    isLoading: !!folderUid && !folderMatchesRoute && (isLoading || isNavigating),
    isError: !!folderUid && !folderMatchesRoute && !isLoading && !isNavigating,
    label: folderMatchesRoute && parentFolder ? parentFolder.fileName : undefined,
  });

  const offline = useFilesOfflinePolling({
    files: queryPublic.files,
    parentFolder: queryPublic.parentFolder,
    authenticatedUser: query.authenticatedUser,
    token: query.token,
    refreshToken: query.refreshToken,
  });

  useFilesWebSocket({
    files: queryPublic.files,
    setFiles: queryPublic.setFiles,
    onFilesUpdated: queryPublic.onFilesUpdated,
  });

  const menuDeps = {
    ...filesActions,
    files: queryPublic.files,
    parentFolder: queryPublic.parentFolder,
    table: query.table,
    filters: {
      orderAsc: queryPublic.filters.orderAsc!,
      orderColumn: queryPublic.filters.orderColumn!,
    },
    message: query.message,
    t: query.t,
    isInFileRoute: queryPublic.isInFileRoute,
    setIsNavigating: queryPublic.setIsNavigating,
    openFolderOrRequestPassword: queryPublic.openFolderOrRequestPassword,
    openFileSafely: queryPublic.openFileSafely,
    copyFileLinkToClipboard: queryPublic.copyFileLinkToClipboard,
    handleDownloadFiles: queryPublic.handleDownloadFiles,
    openRightPanel: queryPublic.openRightPanel,
    getFilePasswordSession: queryPublic.getFilePasswordSession,
    onFilesCreated: queryPublic.onFilesCreated,
    onFilesUpdated: queryPublic.onFilesUpdated,
    onFilesDeleted: queryPublic.onFilesDeleted,
    setFiles: queryPublic.setFiles,
    setTotalFiles: queryPublic.setTotalFiles,
    setSelectedTableKeys: queryPublic.setSelectedTableKeys,
    setPrependFileData: queryPublic.setPrependFileData,
    setFilesOfflinePollingBump: offline.setFilesOfflinePollingBump,
  } as UseFilesMenuDeps;

  const { handleFileMenuAction } = useFilesMenuAction(menuDeps);

  useFilesKeyboardNavigation({
    enabled: true,
    fileActionContext: { ...queryPublic.filesActionsContextBase, files: queryPublic.files },
    files: queryPublic.files,
    handleFileMenuAction,
    isGridMode: queryPublic.isFilesListInGridMode,
    onToggleGridList: () =>
      queryPublic.setIsFilesListInGridMode(!queryPublic.isFilesListInGridMode),
    selectedTableKeys: queryPublic.selectedTableKeys,
    setSelectedTableKeys: queryPublic.setSelectedTableKeys,
  });

  return {
    ...queryPublic,
    ...offline,
    handleFileMenuAction,
  };
}

export default useFiles;
